import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BLANK_LINE = { sku_id: "", quantity: "", serial_number: "" };
const needsSerial = (catalog, id) =>
  /PPF|FILM|SUN/i.test((catalog.find((c) => c.id === id) || {}).category || "");

// Quick material purchase order (studio → HQ), openable from the dashboard without navigating.
export const QuickOrderDialog = ({ onCreated, trigger }) => {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [lines, setLines] = useState([{ ...BLANK_LINE }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && catalog.length === 0) {
      api.get("/catalog").then(({ data }) => setCatalog(data || [])).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLine = (idx, key, val) =>
    setLines((ls) => ls.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));

  const submit = async () => {
    const items = lines
      .filter((l) => l.sku_id && Number(l.quantity) > 0)
      .map((l) => ({
        sku_id: l.sku_id, quantity: Number(l.quantity),
        serial_number: needsSerial(catalog, l.sku_id) ? (l.serial_number || "") : "",
      }));
    if (items.length === 0) return toast.error("Add at least one SKU with a quantity");
    setBusy(true);
    try {
      await api.post("/material-orders", { items });
      toast.success("Purchase order submitted to HQ");
      setLines([{ ...BLANK_LINE }]);
      setOpen(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLines([{ ...BLANK_LINE }]); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="quick-order-dialog">
        <DialogHeader>
          <DialogTitle>New material order</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {lines.map((l, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2">
              <Select value={l.sku_id} onValueChange={(v) => setLine(idx, "sku_id", v)}>
                <SelectTrigger className="min-w-[200px] flex-1" data-testid={`quick-order-sku-${idx}`}>
                  <SelectValue placeholder="Select SKU" />
                </SelectTrigger>
                <SelectContent>
                  {catalog.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — {money(c.hq_sale_price)}/{c.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="w-24"
                placeholder="Qty"
                inputMode="decimal"
                data-testid={`quick-order-qty-${idx}`}
                value={l.quantity}
                onChange={(e) => setLine(idx, "quantity", e.target.value)}
              />
              {lines.length > 1 && (
                <Button size="sm" variant="ghost" className="text-rose-600"
                  data-testid={`quick-order-remove-${idx}`}
                  onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              {needsSerial(catalog, l.sku_id) && (
                <Input
                  className="w-full"
                  placeholder="PPF / sun-film serial number (for warranty)"
                  data-testid={`quick-order-serial-${idx}`}
                  value={l.serial_number || ""}
                  onChange={(e) => setLine(idx, "serial_number", e.target.value)}
                />
              )}
            </div>
          ))}
          <Button size="sm" variant="outline" data-testid="quick-order-add-line"
            onClick={() => setLines((ls) => [...ls, { ...BLANK_LINE }])}>
            <Plus className="mr-1.5 h-4 w-4" /> Add another SKU
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="quick-order-submit" onClick={submit} disabled={busy}>
            Submit order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
